import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MonthPicker from './MonthPicker';
import { ServerIcon } from '../utils/serverIcons';
import { rankModerators, getRatingColorLevel } from '../utils/periodSummaryRating';
import { applyMrpRatingSettings } from '../utils/mrpRatingSettings';
import { exportPeriodSummaryToExcel } from '../utils/exportPeriodSummaryExcel';
import PeriodSummaryRatingBreakdown from './PeriodSummaryRatingBreakdown';
import { MRP_METRICS } from '../utils/mrpStatisticsMetrics';
import { managersStatsPath } from '../utils/statisticsUrlParams';
import { MetricLegend } from './StatisticsManagersTable';
import './StatisticsManagers.css';
import './StatisticsManagersExtended.css';
import './StatisticsPeriodSummary.css';

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const FILTER_STORAGE_KEY = 'sp-period-summary-filters-v1';

function getMaxPeriodParts() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function clampFilterParts({ periodType, year, month }) {
  const max = getMaxPeriodParts();
  let y = Number(year);
  let m = Number(month);
  if (!Number.isFinite(y)) y = max.year;
  if (!Number.isFinite(m) || m < 1 || m > 12) m = max.month;

  const pType = periodType === 'year' ? 'year' : 'month';
  const sType = 'mrp';

  if (pType === 'year') {
    if (y > max.year) y = max.year;
    return { statType: sType, periodType: pType, year: y, month: max.month };
  }

  if (y > max.year || (y === max.year && m > max.month)) {
    y = max.year;
    m = max.month;
  }
  return { statType: sType, periodType: pType, year: y, month: m };
}

function readStoredFilters() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    return clampFilterParts(JSON.parse(raw));
  } catch {
    return null;
  }
}

function readFiltersFromSearchParams(searchParams) {
  if (!searchParams.get('type') && !searchParams.get('year')) return null;
  return clampFilterParts({
    statType: searchParams.get('type'),
    periodType: searchParams.get('period'),
    year: searchParams.get('year'),
    month: searchParams.get('month'),
  });
}

function getInitialFilters() {
  if (typeof window === 'undefined') {
    const max = getMaxPeriodParts();
    return clampFilterParts({ statType: 'mrp', periodType: 'month', year: max.year, month: max.month });
  }
  const fromUrl = readFiltersFromSearchParams(new URLSearchParams(window.location.search));
  if (fromUrl) return fromUrl;
  const fromStorage = readStoredFilters();
  if (fromStorage) return fromStorage;
  const max = getMaxPeriodParts();
  return clampFilterParts({ statType: 'mrp', periodType: 'month', year: max.year, month: max.month });
}

function buildFilterSearchParams({ statType, periodType, year, month }) {
  const params = new URLSearchParams();
  params.set('type', statType);
  params.set('period', periodType);
  params.set('year', String(year));
  if (periodType === 'month') params.set('month', String(month));
  return params;
}

const DESKTOP_EXPORT_MQ = '(min-width: 901px)';

function RatingCell({ rating, ratingLabel, onOpen }) {
  if (ratingLabel === '—') {
    return (
      <td className="mrp-c sp-period-rating" data-label="Рейтинг">
        <span className="sp-rating sp-rating--empty">—</span>
      </td>
    );
  }
  const level = getRatingColorLevel(rating);
  return (
    <td className="mrp-c sp-period-rating" data-label="Рейтинг">
      <button
        type="button"
        className={`sp-rating-btn sp-rating sp-rating--${level}`}
        onClick={onOpen}
        title="Подробный разбор рейтинга"
      >
        {ratingLabel}
      </button>
    </td>
  );
}

const StatisticsPeriodSummary = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFiltersRef = useRef(null);
  if (!initialFiltersRef.current) {
    initialFiltersRef.current = getInitialFilters();
  }
  const initial = initialFiltersRef.current;

  const [statType] = useState('mrp');
  const [periodType, setPeriodType] = useState(initial.periodType);
  const [periodDate, setPeriodDate] = useState(
    () => new Date(initial.year, initial.month - 1, 1)
  );
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [servers, setServers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [canExport, setCanExport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_EXPORT_MQ).matches
  );
  const [dataFiltersKey, setDataFiltersKey] = useState('');
  const [ratingBreakdownMod, setRatingBreakdownMod] = useState(null);
  const [ratingConfigVersion, setRatingConfigVersion] = useState(0);
  const statsLoadIdRef = useRef(0);

  const periodYear = periodDate.getFullYear();
  const periodMonth = periodDate.getMonth() + 1;
  const filtersKey = `${statType}:${periodType}:${periodYear}:${periodMonth}`;

  useEffect(() => {
    setRatingBreakdownMod(null);
  }, [filtersKey]);

  const showRating = true;

  const ratingOpts = useMemo(
    () => ({ statType, periodType, year: periodYear, month: periodMonth }),
    [statType, periodType, periodYear, periodMonth]
  );

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_EXPORT_MQ);
    const onChange = (e) => setCanExport(e.matches);
    onChange(mq);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const loadServers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/servers', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setServers(await res.json());
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  useEffect(() => {
    const loadMrpRatingSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/app-settings', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.mrp_rating) {
            applyMrpRatingSettings(data.mrp_rating);
            // applyMrpRatingSettings пишет в module-scope — без setState таблица не пересчитает рейтинг
            setRatingConfigVersion((v) => v + 1);
          }
        }
      } catch (e) {
        console.error('Error loading MRP rating settings:', e);
      }
    };
    loadMrpRatingSettings();
  }, []);

  useEffect(() => {
    const next = buildFilterSearchParams({
      statType,
      periodType,
      year: periodYear,
      month: periodMonth,
    });
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
    try {
      sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ statType, periodType, year: periodYear, month: periodMonth })
      );
    } catch {
      /* ignore quota */
    }
  }, [statType, periodType, periodYear, periodMonth, searchParams, setSearchParams]);

  useEffect(() => {
    const loadId = ++statsLoadIdRef.current;
    const requestKey = filtersKey;
    let cancelled = false;

    const run = async () => {
      setLoading(true);

      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          statType,
          periodType,
          year: String(periodYear),
        });
        if (periodType === 'month') params.set('month', String(periodMonth));

        const res = await fetch(`/api/statistics/period/all?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });

        if (cancelled || loadId !== statsLoadIdRef.current) return;

        const data = res.ok ? await res.json() : [];
        setModerators(Array.isArray(data) ? data : []);
        setDataFiltersKey(requestKey);
      } catch (e) {
        console.error('Error loading period stats:', e);
        if (cancelled || loadId !== statsLoadIdRef.current) return;
        setModerators([]);
        setDataFiltersKey(requestKey);
      } finally {
        if (!cancelled && loadId === statsLoadIdRef.current) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [statType, periodType, periodYear, periodMonth, filtersKey]);

  const serverIconMap = useMemo(() => {
    const m = {};
    servers.forEach((s) => {
      m[`RU${s.server_number}_${s.server_name}`] = s.icon_url || null;
    });
    return m;
  }, [servers]);

  const periodLabel = useMemo(() => {
    if (periodType === 'month') return `${MONTH_NAMES[periodMonth - 1]} ${periodYear}`;
    return String(periodYear);
  }, [periodType, periodMonth, periodYear]);

  const ranked = useMemo(() => {
    if (loading || dataFiltersKey !== filtersKey) return [];

    const q = searchQuery.trim().toLowerCase();
    const list = q
      ? moderators.filter(
          (m) =>
            (m.nickname || '').toLowerCase().includes(q) ||
            (m.angry_space || '').toLowerCase().includes(q) ||
            (m.server || '').toLowerCase().includes(q)
        )
      : [...moderators];

    return rankModerators(list, ratingOpts);
  }, [moderators, searchQuery, ratingOpts, loading, dataFiltersKey, filtersKey, ratingConfigVersion]);

  const getMaxPeriodDate = useCallback(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, []);

  const clampPeriodDate = useCallback(
    (date) => {
      const max = getMaxPeriodDate();
      const maxYear = max.getFullYear();
      const maxMonth = max.getMonth();

      if (periodType === 'month') {
        let y = date.getFullYear();
        let m = date.getMonth();
        if (y > maxYear || (y === maxYear && m > maxMonth)) {
          return new Date(maxYear, maxMonth, 1);
        }
        return new Date(y, m, 1);
      }

      const y = date.getFullYear();
      if (y > maxYear) return new Date(maxYear, 0, 1);
      return new Date(y, 0, 1);
    },
    [periodType, getMaxPeriodDate]
  );

  const setPeriodDateClamped = useCallback(
    (date) => {
      setPeriodDate(clampPeriodDate(date));
    },
    [clampPeriodDate]
  );

  useEffect(() => {
    setPeriodDate((prev) => clampPeriodDate(prev));
  }, [periodType, clampPeriodDate]);

  const isCurrentPeriod = useMemo(() => {
    const max = getMaxPeriodDate();
    if (periodType === 'month') {
      return periodYear === max.getFullYear() && periodMonth === max.getMonth() + 1;
    }
    return periodYear === max.getFullYear();
  }, [periodType, periodYear, periodMonth, getMaxPeriodDate]);

  const changePeriod = (delta) => {
    if (delta > 0 && isCurrentPeriod) return;
    if (periodType === 'month') {
      setPeriodDateClamped(new Date(periodYear, periodMonth - 1 + delta, 1));
    } else {
      setPeriodDateClamped(new Date(periodYear + delta, 0, 1));
    }
  };

  const goToCurrentPeriod = () => {
    setPeriodDate(getMaxPeriodDate());
  };

  const currentPeriodLabel = periodType === 'month' ? 'Текущий месяц' : 'Текущий год';

  const handleExportCurrent = () => {
    if (!canExport || loading || exporting || !moderators.length) return;
    const ok = exportPeriodSummaryToExcel({
      moderators,
      statType,
      ratingOpts,
      periodLabel,
      periodType,
      year: periodYear,
      month: periodMonth,
    });
    if (!ok) window.alert('Нет данных для экспорта');
  };

  const currentTypeLabel = 'Manager';

  const parseServerRu = (server) => {
    if (!server) return '—';
    const i = server.indexOf('_');
    return i >= 0 ? server.slice(0, i) : server;
  };

  return (
    <div className="period-summary-container">
      <div className="sp-toolbar">
        <div className="sp-toolbar-head">
          <div className="sp-period-mode" role="group" aria-label="Тип периода">
            <button
              type="button"
              className={periodType === 'month' ? 'is-active' : ''}
              onClick={() => setPeriodType('month')}
            >
              Месяц
            </button>
            <button
              type="button"
              className={periodType === 'year' ? 'is-active' : ''}
              onClick={() => setPeriodType('year')}
            >
              Год
            </button>
          </div>

          <div className="sp-period-nav">
            <button type="button" className="sp-icon-btn" onClick={() => changePeriod(-1)} aria-label="Назад">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div className={`sp-period-label ${periodType === 'year' ? 'sp-period-label--year' : ''}`}>
              {periodType === 'month' ? (
                <MonthPicker
                  selectedDate={periodDate}
                  onDateChange={setPeriodDateClamped}
                  label={periodLabel}
                  maxDate={getMaxPeriodDate()}
                />
              ) : (
                <span className="sp-period-text" aria-live="polite">
                  {periodLabel}
                </span>
              )}
            </div>

            <button
              type="button"
              className="sp-icon-btn"
              onClick={() => changePeriod(1)}
              disabled={isCurrentPeriod}
              aria-label="Вперёд"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {!isCurrentPeriod && (
              <button
                type="button"
                className="sp-nav-current-btn"
                onClick={goToCurrentPeriod}
                title={currentPeriodLabel}
                aria-label={currentPeriodLabel}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            )}
          </div>

          {canExport && (
          <button
            type="button"
            className="sp-btn-export sp-toolbar-export"
            onClick={handleExportCurrent}
            disabled={loading || exporting || moderators.length === 0}
            title="Скачать сводку в Excel"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span className="sp-toolbar-export-label">{exporting ? '…' : 'Экспорт'}</span>
          </button>
          )}
        </div>

        <div className="sp-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3-3" />
          </svg>
          <input
            type="search"
            placeholder="Поиск по нику, серверу, static ID"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="sp-table-wrap sp-period-board-wrap">
        {loading ? (
          <div className="sp-state">
            <div className="sp-spinner" />
            <span>Загрузка</span>
          </div>
        ) : ranked.length === 0 ? (
          <div className="sp-state">Нет данных за выбранный период</div>
        ) : (
          <section className="mrp-board sp-period-board">
            <div className="mrp-board__bar">
              <div className="mrp-board__heading">
                <h3 className="mrp-board__title">{currentTypeLabel} · {periodLabel}</h3>
                <span className="mrp-board__count">{ranked.length}</span>
              </div>
              <MetricLegend metrics={MRP_METRICS} />
            </div>
            <div className="mrp-board__frame">
              <div className="mrp-board__scroll">
                <table key={dataFiltersKey || filtersKey} className="mrp-sheet sp-period-sheet">
                  <thead>
                    <tr>
                      <th className="mrp-h sp-period-h--rank">#</th>
                      <th className="mrp-h mrp-h--server">Сервер</th>
                      <th className="mrp-h mrp-h--nick">Никнейм</th>
                      <th className="mrp-h sp-period-h--as">Angry Space</th>
                      {MRP_METRICS.map((metric) => (
                        <th
                          key={metric.key}
                          className={`mrp-h mrp-h--metric mrp-h--${metric.group}`}
                          title={metric.label}
                        >
                          <span className="mrp-h__label mrp-h__label--full">{metric.label}</span>
                          <span className="mrp-h__label mrp-h__label--short">{metric.short}</span>
                        </th>
                      ))}
                      {showRating && <th className="mrp-h sp-period-h--discipline">Выговоры</th>}
                      {showRating && <th className="mrp-h sp-period-h--rating">Рейтинг</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {ranked.map((mod) => {
                      const serverRu = parseServerRu(mod.server);
                      const nickTitle = mod.restored_at
                        ? `${mod.nickname} · восстановлен ${new Date(mod.restored_at).toLocaleDateString('ru-RU')}`
                        : mod.nickname;
                      return (
                        <tr
                          key={mod.discord_id}
                          className={[
                            mod.deleted_at ? 'is-deleted' : '',
                            mod.restored_at && !mod.deleted_at ? 'is-restored' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <td className="mrp-c sp-period-c--rank"><span className="sp-rank">{mod.rank}</span></td>
                          <td className="mrp-c mrp-c--server">
                            <div className="mrp-server" title={mod.server}>
                              <div className="mrp-server__top">
                                <ServerIcon iconUrl={serverIconMap[mod.server]} size={18} className="mrp-server__icon" />
                                <span className="mrp-server__ru">{serverRu}</span>
                              </div>
                            </div>
                          </td>
                          <td className="mrp-c mrp-c--nick">
                            <button
                              type="button"
                              className="mrp-nick mrp-nick--link"
                              title={nickTitle ? `${nickTitle} · открыть статистику` : 'Открыть статистику'}
                              onClick={() => {
                                if (!mod.discord_id) return;
                                navigate(managersStatsPath({ moderatorId: mod.discord_id }));
                              }}
                            >
                              {mod.nickname || '—'}
                              {mod.restored_at && !mod.deleted_at && <span className="sp-restored-badge">↺</span>}
                            </button>
                          </td>
                          <td className="mrp-c sp-period-c--as">{mod.angry_space || '—'}</td>
                          {MRP_METRICS.map((metric) => {
                            const value = Number(mod.totalMetrics?.[metric.key]) || 0;
                            return (
                              <td key={metric.key} className={`mrp-c mrp-c--metric mrp-c--${metric.group}`}>
                                <span className={`mrp-v mrp-v--lg ${value > 0 ? 'has-value' : ''}`}>
                                  {value > 0 ? value : '—'}
                                </span>
                              </td>
                            );
                          })}
                          {showRating && (
                            <td className={`mrp-c sp-period-discipline ${mod.totalReprimands > 0 ? 'has-penalty' : ''}`}>
                              {mod.totalReprimands || '—'}
                            </td>
                          )}
                          {showRating && (
                            <RatingCell
                              rating={mod.rating}
                              ratingLabel={mod.ratingLabel}
                              onOpen={() => setRatingBreakdownMod(mod)}
                            />
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </div>

      {showRating && ratingBreakdownMod && (
        <PeriodSummaryRatingBreakdown
          mod={ratingBreakdownMod}
          statType={statType}
          periodType={periodType}
          year={periodYear}
          month={periodMonth}
          cohort={ranked}
          onClose={() => setRatingBreakdownMod(null)}
        />
      )}
    </div>
  );
};

export default StatisticsPeriodSummary;
